import { useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { toJS } from 'mobx'
import { observer } from 'mobx-react-lite'

import { RoutingPaths } from 'app/routes/routes.ts'

import { FavoritesEmpty } from 'pages/avia/favorites/components/favorites-empty'
import { FavoritesRemoveModal } from 'pages/avia/favorites/components/favorites-remove-modal'

import { favoritesStore } from 'widgets/avia/favorites/favorites-store.ts'

import { prepareCabinClass } from 'entities/favorite/lib/get-search-form'
import findTicketStore from 'entities/ticket/models/find-ticket/find-ticket-store.ts'
import { CardOfTicket } from 'entities/ticket/ui/card-of-ticket/ui'
import userStore from 'entities/user/module/user-store'

import { Favorite } from 'shared/api/avia/favorites/model.ts'
import { prepareShowListTicket } from 'shared/lib/ticketRender.ts'
import { BottomNavigation } from 'shared/ui/bottom-navigation'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderResetButton } from 'shared/ui/header-reset-button'
import { Loader } from 'shared/ui/loader'

import s from './styles.module.scss'

const FavoritesPage = observer(() => {
  const { t } = useTranslation()
  const { openDialog } = useDialogBottomSheet()
  const navigate = useNavigate()
  const user = userStore.account

  const favorites = favoritesStore.favoritesList
  const loading = favoritesStore.loading

  const passengersCount = findTicketStore.getCountOfPassengers()
  const hasFavorites = !!favorites?.length

  const onClickDetail = (ticket: Favorite) => {
    const data = toJS(ticket.search_params)

    favoritesStore.resetFavoritesDetail()

    navigate(
      `${RoutingPaths.avia.favorites.detail.path(ticket.favorite_search_key)}`,
      {
        state: {
          ...data,
          cabin_class: prepareCabinClass(data.cabin_class as string),
        },
      },
    )
  }

  const onRemoveFavorites = async () => {
    openDialog({
      child: <FavoritesRemoveModal />,
      isClickable: false,
      isDialog: false,
    })
  }

  const onBack = () => {
    if (window.history.length > 1) {
      navigate(-1)

      return
    }

    navigate(RoutingPaths.path())
  }

  useEffect(() => {
    if (user) {
      void favoritesStore.fetchFavorites()
    }
  }, [user])

  return (
    <div className={s.container}>
      <HeaderResetButton
        title={t('favorites')}
        variant={'grey'}
        resetTitle={'favorites_block.clean'}
        onBack={onBack}
        isCanDisable={hasFavorites}
        onReset={onRemoveFavorites}
      />

      {loading ? (
        <div className={s.loader}>
          <Loader />
        </div>
      ) : hasFavorites ? (
        favorites?.map((ticket) => {
          const preparedTicket = prepareShowListTicket(
            {
              ...ticket.flight_details,
              favorite_search_key: ticket?.favorite_search_key,
              is_30_minutes_left: ticket?.is_30_minutes_left,
              search_params: ticket?.search_params,
            },
            passengersCount,
          )
          return (
            <div
              className={s.item}
              key={ticket.favorite_search_key}
            >
              <CardOfTicket
                isMbonus
                isFavorites
                ticket={preparedTicket}
                searchParams={ticket.search_params}
                showWarning={preparedTicket?.is_30_minutes_left}
                onClick={() => onClickDetail(ticket)}
              />
            </div>
          )
        })
      ) : (
        <FavoritesEmpty />
      )}

      <div className={s.footer} />

      <BottomNavigation activeId="favorites" />
    </div>
  )
})

export default FavoritesPage
